import { useEffect, useRef, useState } from "react"

interface SortOption {
  value: string
  label: string
}

interface FilterPanelProps {
  open: boolean
  onClose: () => void
  labels: {
    search_placeholder: string
    sort_label: string
    price_label: string
    min_price: string
    max_price: string
    apply: string
    reset: string
  }
  initial: {
    query: string
    sort: string
    minPrice: number
    maxPrice: number
    availableMaxPrice: number
  }
  sortOptions: SortOption[]
  showPrice?: boolean
  onApply: (filters: {
    query: string
    sort: string
    minPrice: number
    maxPrice: number
  }) => void
  onReset: () => void
}

export default function FilterPanel({
  open,
  onClose,
  labels,
  initial,
  sortOptions,
  showPrice = true,
  onApply,
  onReset,
}: FilterPanelProps) {
  const [query, setQuery] = useState(initial.query)
  const [sort, setSort] = useState(initial.sort)
  const [minPrice, setMinPrice] = useState<string>(String(initial.minPrice || ""))
  const [maxPrice, setMaxPrice] = useState<string>(String(initial.maxPrice || ""))
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setQuery(initial.query)
    setSort(initial.sort)
    setMinPrice(String(initial.minPrice || ""))
    setMaxPrice(String(initial.maxPrice || ""))
  }, [initial.query, initial.sort, initial.minPrice, initial.maxPrice])

  useEffect(() => {
    if (!open) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [open, onClose])

  if (!open) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onApply({
      query: query.trim(),
      sort,
      minPrice: minPrice === "" ? 0 : Number(minPrice),
      maxPrice: maxPrice === "" ? initial.availableMaxPrice : Number(maxPrice),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-black/40"
      />
      <div
        ref={panelRef}
        className="relative w-full max-w-md h-full bg-white shadow-xl overflow-y-auto"
      >
        <form onSubmit={handleSubmit} className="p-8 space-y-8 font-unsf-title">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-contrast">
              {labels.sort_label}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-contrast cursor-pointer"
            >
              <i className="fa-solid fa-xmark text-2xl" />
            </button>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="filter-query"
              className="block text-sm font-bold text-contrast"
            >
              {labels.search_placeholder}
            </label>
            <input
              id="filter-query"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={labels.search_placeholder}
              className="w-full border border-contrast rounded-md px-4 py-2 text-content focus:outline-none focus:ring-2 focus:ring-contrast"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="filter-sort"
              className="block text-sm font-bold text-contrast"
            >
              {labels.sort_label}
            </label>
            <select
              id="filter-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full border border-contrast rounded-md px-4 py-2 text-content focus:outline-none focus:ring-2 focus:ring-contrast bg-white"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {showPrice && (
            <div className="space-y-2">
              <p className="text-sm font-bold text-contrast">
                {labels.price_label}
              </p>
              <div className="flex gap-3">
                <input
                  type="number"
                  min={0}
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder={labels.min_price}
                  aria-label={labels.min_price}
                  className="w-full border border-contrast rounded-md px-4 py-2 text-content focus:outline-none focus:ring-2 focus:ring-contrast"
                />
                <input
                  type="number"
                  min={0}
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder={labels.max_price}
                  aria-label={labels.max_price}
                  className="w-full border border-contrast rounded-md px-4 py-2 text-content focus:outline-none focus:ring-2 focus:ring-contrast"
                />
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-contrast text-light rounded-md px-6 py-3 text-sm font-medium hover:bg-black transition-colors"
            >
              {labels.apply}
            </button>
            <button
              type="button"
              onClick={onReset}
              className="flex-1 border border-contrast text-contrast rounded-md px-6 py-3 text-sm font-medium hover:bg-contrast hover:text-light transition-colors"
            >
              {labels.reset}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
